import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import ReactGA from "react-ga4";
import { ReduxState } from "../../../../redux";
import { setSelectedDocumentId } from "../../../../redux/reducers/TransactionManager";
import SelectWithBadge from "./SelectWithBadge";
import { getDocumentName } from "../../Analyze/AnalyzeTransactionsTable/utils";
import { fetchJobDocuments } from "src/redux/reducers/JobDocument";
import { isNullOrUndefined } from "src/utils/utils";
import { Alert, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import {
  deleteDocumentFromJob,
  deleteEntityFromTagLevel,
} from "src/docuclipper/api";
import { createAlert } from "src/redux/reducers/Alerts";
import { fetchTagLevels } from "src/redux/reducers/TagLevel";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { fetchTotals } from "src/redux/reducers/Reconciler";
import { clearJob } from "src/redux/reducers/TemplateField";
import { clearJobData } from "src/redux/reducers/JobData";

type FieldNameType =
  | "bankMode"
  | "invoiceMode"
  | "formMode"
  | "generic"
  | "template";

const getFieldNameFromUrl = (
  pathname: string,
  search: string = ""
): FieldNameType => {
  const params = new URLSearchParams(search);
  const docType = params.get("docType");
  const mode = params.get("mode");
  // When on extractdata tree view, docType in URL is the source of truth for document type
  if (docType === "invoices") return "invoiceMode";
  if (docType === "forms") return "formMode";
  if (docType === "brokerage") return "brokerageMode";
  // Other Documents: templates view/list = template, automatic view/list = generic
  if (docType === "other") {
    return mode === "templates" || mode === "template-documents"
      ? "template"
      : "generic";
  }
  if (docType === "bank" || ["reconcile", "categorize", "analyze", "fraud", "documents"].includes(mode || "")) {
    return "bankMode";
  }
  if (pathname.includes("/transactions")) {
    return "bankMode";
  } else if (pathname.includes("/form")) {
    return "formMode";
  } else if (pathname.includes("/templateDocs")) {
    return "template";
  }
  // Tree view (extractdata) without docType in URL defaults to bank
  if (pathname.includes("extractdata")) {
    return "bankMode";
  }
  return "generic"; // default case
};

const DocumentSelector: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { selectedDocumentId, reconciled, badDates } = useSelector(
    (state: ReduxState) => state.TransactionManager
  );
  const { data: jobDocs } = useSelector(
    (state: ReduxState) => state.JobDocument
  );
  const { tagLevels } = useSelector((state: ReduxState) => state.TagLevel);
  // Detect check-mode jobs so the empty-state can tell users their PDF didn't
  // look like check images (rating 11007, job 2568910: a 1-page bank statement
  // uploaded via the checkImages flow extracted 0 checks and the user got the
  // generic "no transactions" copy with no hint that they used the wrong flow).
  const { job } = useSelector((state: ReduxState) => state.JobData);
  const isCheckImagesJob = (() => {
    if (!job?.metadata) return false;
    try {
      const meta = JSON.parse(job.metadata);
      return meta?.subType === "checkImages";
    } catch {
      return false;
    }
  })();

  React.useEffect(() => {
    dispatch(fetchTagLevels());
  }, []);
  // Parse query parameters
  const queryParams = new URLSearchParams(location.search);
  const newDocumentsParam = queryParams.get("newDocuments");
  const l1 = queryParams.get("l1");
  const l2 = queryParams.get("l2");
  const jobId = queryParams.get("jobId");
  const allowedDocumentIds = newDocumentsParam
    ? newDocumentsParam.split(",")
    : null;


  const handleDeleteDocument = async (documentId: string) => {
    confirmAlert({
      title: "Confirm Document Deletion",
      message: "Are you sure you want to delete this document?",
      buttons: [
        {
          label: "Yes",
          onClick: async () => {
            try {
              if (l1 && l2) {
                const tagId = tagLevels?.root?.[l1]?.[l2]?.metadata?.tagId;
                if (!tagId) {
                  dispatch(
                    createAlert({
                      message: "Error deleting document. Please try again.",
                      timeout: 0,
                      type: "error",
                    })
                  );
                  return;
                }
                await deleteEntityFromTagLevel(tagId, selectedDocumentId);
              } else if (jobId) {
                await deleteDocumentFromJob(jobId, documentId);
              }
              // Refresh documents list
              dispatch(
                fetchJobDocuments({
                  fieldName: getFieldNameFromUrl(location.pathname, location.search),
                })
              );
              dispatch(fetchTotals({}));
              // Clear selection if deleted document was selected
              if (selectedDocumentId === documentId) {
                dispatch(setSelectedDocumentId(null));
              }
            } catch (error) {
              console.error("Error deleting document:", error);
            }
          },
        },
        {
          label: "No",
          onClick: () => {
            // Do nothing if user cancels deletion
          },
        },
      ],
    });
  };

  // useEffect(() => {
  //   if (isNullOrUndefined(jobDocs)) {
  //     dispatch(fetchJobDocuments({}));
  //   }
  // }, [jobDocs, dispatch, l1, l2, jobId]);

  // Backend returns documents sorted by extracted statement startDate
  // ascending (Jan → Dec, which is the natural reading order). For forensic /
  // multi-year workflows the user may want to find a specific statement
  // quickly by filename or by upload order instead. The sort toggle here
  // re-orders the dropdown options client-side. Default ("date") preserves
  // the historic backend order.
  type DocSortKey = "date" | "name" | "pages";
  const docSortLsKey = "docuclipper.ui.documentSelector.sortBy";
  const [docSortBy, setDocSortBy] = React.useState<DocSortKey>(() => {
    const saved =
      (typeof localStorage !== "undefined" && localStorage.getItem(docSortLsKey)) ||
      "date";
    return (saved === "name" || saved === "pages" ? saved : "date") as DocSortKey;
  });

  const newDocIdSet = new Set(allowedDocumentIds || []);
  const uniqueDocumentIds = Array.from(
    new Set((jobDocs || []).map((item) => item.documentId))
  );

  const documentOptions = uniqueDocumentIds.map((docId) => {
    const jobDoc = (jobDocs || []).find((jd) => jd.documentId === docId);
    return {
      value: docId,
      label: getDocumentName(jobDocs || [], docId),
      reconciled: reconciled.documentIds[(docId as any) || ""],
      isBadDate: badDates.documentIds[(docId as any) || ""],
      hasNoTransactions: jobDoc?.hasNoTransactions === true,
      hasPartialExtraction: jobDoc?.hasPartialExtraction === true,
      isNew: newDocIdSet.has(docId),
      _sortName: (getDocumentName(jobDocs || [], docId) || "").toLowerCase(),
      _sortPages: parseInt((jobDoc as any)?.numPages || "0", 10) || 0,
    };
  });

  if (docSortBy === "name") {
    documentOptions.sort((a, b) => a._sortName.localeCompare(b._sortName));
  } else if (docSortBy === "pages") {
    documentOptions.sort((a, b) => b._sortPages - a._sortPages);
  }
  // "date" → leave in backend order (already chronological).

  const selectedDocument = documentOptions.filter(
    (x) => x.value === selectedDocumentId
  );

  const selectedJobDoc = (jobDocs || []).find(
    (jd) => jd.documentId === selectedDocumentId
  );
  const selectedHasNoTransactions = selectedJobDoc?.hasNoTransactions === true;
  const selectedHasPartialExtraction =
    !selectedHasNoTransactions && selectedJobDoc?.hasPartialExtraction === true;

  // Track which `?newDocuments=` value we've already auto-selected for, so we
  // honor user clicks afterward instead of fighting them on every render.
  const autoSelectedForNewDocsRef = React.useRef<string | null>(null);

  useEffect(() => {
    if (documentOptions.length === 0) return;

    // When the URL signals a fresh upload, prefer one of the new docs over
    // whatever was previously selected. Only do this once per newDocuments
    // value so the user can still click around afterward.
    if (
      newDocumentsParam &&
      autoSelectedForNewDocsRef.current !== newDocumentsParam
    ) {
      const firstNew = documentOptions.find((opt) => opt.isNew);
      if (firstNew) {
        autoSelectedForNewDocsRef.current = newDocumentsParam;
        if (selectedDocumentId !== firstNew.value) {
          dispatch(setSelectedDocumentId(firstNew.value));
        }
        return;
      }
    }

    if (!selectedDocumentId) {
      dispatch(setSelectedDocumentId(documentOptions[0].value));
    }
  }, [selectedDocumentId, documentOptions, newDocumentsParam, dispatch]);

  useEffect(() => {
    if (selectedHasNoTransactions && selectedDocumentId) {
      ReactGA.event("has_no_transactions_shown", {
        document_id: String(selectedDocumentId),
      });
    }
  }, [selectedHasNoTransactions, selectedDocumentId]);

  React.useEffect(() => {
    dispatch(fetchTotals({}));
    dispatch(
      fetchJobDocuments({
        fieldName: getFieldNameFromUrl(location.pathname, location.search),
      })
    );
  }, [location.pathname, location.search]);

  const currentFieldName = getFieldNameFromUrl(location.pathname, location.search);

  return (
    <div className="flex-column">
      <div className="d-flex align-items-center mb-2">
        <span className="font-weight-bold mr-1">
          Select Document ({uniqueDocumentIds.length} available) ▾
        </span>
        <span className="text-muted small ml-3">Sort by:</span>
        <select
          data-cy="document-selector-sort-by"
          className="form-control form-control-sm ml-1"
          style={{ width: "auto", display: "inline-block" }}
          value={docSortBy}
          onChange={(e) => {
            const v = e.target.value as DocSortKey;
            setDocSortBy(v);
            try { localStorage.setItem(docSortLsKey, v); } catch (_) {}
          }}
        >
          <option value="date">Statement date</option>
          <option value="name">Filename (A→Z)</option>
          <option value="pages">Page count (high→low)</option>
        </select>
      </div>
      <div className="d-flex align-items-center" style={{ width: "95%" }}>
        <div className="flex-grow-1">
          <SelectWithBadge
            value={selectedDocument.length > 0 ? selectedDocument[0] : null}
            onChange={(e) => {
              dispatch(setSelectedDocumentId(e.value));
            }}
            options={documentOptions}
          />
          {!selectedDocumentId && (
            <small>
              <div className="text-danger">Please select a document</div>
            </small>
          )}
        </div>
        {selectedDocumentId && (
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id="delete-tooltip">Delete document</Tooltip>}
          >
            <Button
              variant="link"
              size="sm"
              className="ml-2"
              onClick={() => handleDeleteDocument(selectedDocumentId)}
            >
              <FontAwesomeIcon icon="trash" />
            </Button>
          </OverlayTrigger>
        )}
      </div>
      {selectedHasNoTransactions &&
        currentFieldName === "bankMode" &&
        !isCheckImagesJob && (
          <Alert variant="info" className="mt-3 mb-0" style={{ width: "95%" }}>
            <strong>No transactions found in this document.</strong> This
            usually means the statement period had no account activity. If you
            expected transactions, check the date range on the uploaded PDF or
            contact support.
          </Alert>
        )}
      {selectedHasNoTransactions &&
        currentFieldName === "bankMode" &&
        isCheckImagesJob && (
          <Alert variant="warning" className="mt-3 mb-0" style={{ width: "95%" }}>
            <strong>This document doesn't look like check images.</strong> The
            check extractor didn't find any checks in this PDF. If this is a
            bank statement, please re-upload it through the bank-statement flow
            (Add Documents &gt; Bank Statements) instead. If you believe this is
            a check image, contact support.
          </Alert>
        )}
      {selectedHasPartialExtraction && (
        <Alert variant="warning" className="mt-3 mb-0" style={{ width: "95%" }}>
          <strong>Some fields couldn't be extracted.</strong> The date or total
          is missing on one or more rows. Please review and correct the data
          before exporting.
        </Alert>
      )}
    </div>
  );
};

export default DocumentSelector;
